import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { registerRequestSchema } from '@perplexicon/validation';
import type { RegisterRequest } from '@perplexicon/validation';

import { assetUrl } from '../../assets/asset-manifest.js';
import { Button, Card, ErrorState, Input } from '../../components/index.js';
import { isApiErrorCode } from '../../lib/errors.js';
import { useRegister } from '../../lib/auth.js';

export function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();
  const {
    register: field,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RegisterRequest>({ resolver: zodResolver(registerRequestSchema) });

  const onSubmit = handleSubmit((values) => {
    register.mutate(values, {
      onSuccess: () => {
        void navigate('/', { replace: true });
      },
      onError: (error) => {
        if (isApiErrorCode(error, 'USERNAME_TAKEN')) {
          setError('username', { message: 'Username already taken.' });
        } else if (isApiErrorCode(error, 'EMAIL_TAKEN')) {
          setError('email', { message: 'Email already in use.' });
        }
      },
    });
  });

  const genericError =
    register.isError &&
    !isApiErrorCode(register.error, 'USERNAME_TAKEN') &&
    !isApiErrorCode(register.error, 'EMAIL_TAKEN') &&
    !isApiErrorCode(register.error, 'VALIDATION_ERROR');

  return (
    <Card variant="parchment">
      <img
        src={assetUrl('decorFlourish')}
        alt=""
        aria-hidden="true"
        width={64}
        height={64}
        className="mb-2 h-7 w-7"
      />
      <h1 className="font-display text-2xl font-semibold text-textPrimary">Create your account</h1>
      <form onSubmit={(e) => void onSubmit(e)} noValidate className="mt-4 flex flex-col gap-4">
        <Input
          label="Username"
          autoComplete="username"
          error={errors.username?.message}
          {...field('username')}
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          error={errors.email?.message}
          {...field('email')}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          hint="At least 12 characters."
          error={errors.password?.message}
          {...field('password')}
        />
        {genericError && <ErrorState message="Registration failed. Please try again." />}
        <Button
          type="submit"
          emphasis="decorated"
          isLoading={register.isPending}
          className="w-full"
        >
          Create account
        </Button>
      </form>
      <p className="mt-4 text-center text-sm text-textSecondary">
        Already have an account?{' '}
        <Link to="/login" className="font-semibold text-link hover:text-linkHover">
          Log in
        </Link>
      </p>
    </Card>
  );
}
